import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { fetchMyTeams, fetchTeam, setActiveTeam } from '../lib/supabase';

const TeamContext = createContext(null);

export function TeamProvider({ children }) {
  const { user, profile } = useAuth();

  const [teams, setTeams]             = useState([]);       // all teams user belongs to
  const [activeTeam, setActiveTeamState] = useState(null);  // current active team object
  const [teamRole, setTeamRole]       = useState(null);     // 'owner' | 'admin' | 'member'
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Load teams when user logs in
  useEffect(() => {
    if (!user) {
      setTeams([]);
      setActiveTeamState(null);
      setTeamRole(null);
      return;
    }
    loadTeams();
  }, [user?.id]);

  // Restore active team from profile
  useEffect(() => {
    if (!profile?.active_team_id || teams.length === 0) return;
    const saved = teams.find(t => t.id === profile.active_team_id);
    if (saved && (!activeTeam || activeTeam.id !== saved.id)) {
      setActiveTeamState(saved);
      setTeamRole(saved.my_role);
    }
  }, [profile?.active_team_id, teams]);

  const loadTeams = useCallback(async () => {
    if (!user) return;
    setLoadingTeams(true);
    try {
      const data = await fetchMyTeams(user.id);
      setTeams(data);
      // Auto-select if only one team and none active yet
      if (data.length === 1 && !activeTeam) {
        setActiveTeamState(data[0]);
        setTeamRole(data[0].my_role);
      }
    } catch (e) {
      console.error('[TeamContext] fetchMyTeams error:', e);
    }
    setLoadingTeams(false);
  }, [user?.id]);

  const switchTeam = useCallback(async (teamId) => {
    if (!user) return;
    if (!teamId) {
      setActiveTeamState(null);
      setTeamRole(null);
      await setActiveTeam(user.id, null);
      return;
    }
    const found = teams.find(t => t.id === teamId);
    if (found) {
      setActiveTeamState(found);
      setTeamRole(found.my_role);
      await setActiveTeam(user.id, teamId);
    }
  }, [user?.id, teams]);

  const refreshActiveTeam = useCallback(async () => {
    if (!activeTeam) return;
    try {
      const updated = await fetchTeam(activeTeam.id);
      setActiveTeamState(prev => ({ ...prev, ...updated }));
      setTeams(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    } catch (e) {
      console.error('refreshActiveTeam error:', e);
    }
  }, [activeTeam?.id]);

  const isTeamAdmin  = teamRole === 'owner' || teamRole === 'admin';
  const isTeamOwner  = teamRole === 'owner';
  const isTeamMember = !!teamRole;

  return (
    <TeamContext.Provider value={{
      teams, activeTeam, teamRole,
      isTeamAdmin, isTeamOwner, isTeamMember,
      loadingTeams,
      loadTeams, switchTeam, refreshActiveTeam,
      setTeams,
    }}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeam must be used inside TeamProvider');
  return ctx;
}